import { useMemo, useState, useReducer, useCallback } from "react";
import MainStore from "./MainStore";
import { HeroIcon } from "./HeroIcon";
import { Select, SelectItem, Button, Input } from "@heroui/react";
import { PencilIcon, TrashIcon, PlayIcon, PlusIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import useDataEvent from "./CustomHook";

const DEFAULT_PROMPT_CATEGORIES = [13, 14, 21, 50, 51];

export default function QuestionEditor({ primitive: initialPrimitive, onRun }) {
    const mainstore = MainStore();
    const [showAdd, setShowAdd] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [editingPromptId, setEditingPromptId] = useState(null);
    const [editText, setEditText] = useState("");
    const [update, forceUpdate] = useReducer((x) => x + 1, 0);

    // Refresh the primitive reference after mutations
    const primitive = useMemo(() => {
        return mainstore.primitive(initialPrimitive.id) ?? initialPrimitive;
    }, [initialPrimitive.id, update]);

    useDataEvent(
        "relationship_update new_primitive set_field",
        [primitive.id, ...(primitive.primitives?.origin?.allIds ?? [])],
        forceUpdate
    );

    const prompts = useMemo(() => {
        return primitive.primitives?.origin?.allItems?.filter(p => p.type === "prompt") ?? [];
    }, [primitive, update]);

    const promptCategories = useMemo(() => {
        const cats = primitive.metadata?.promptCategories?.length
            ? primitive.metadata.promptCategories
            : DEFAULT_PROMPT_CATEGORIES;
        return cats.map(id => mainstore.category(id)).filter(Boolean);
    }, [primitive]);

    const importPrimitives = primitive.primitives?.imports?.allItems
    const importCount = mainstore.uniquePrimitives(importPrimitives.flatMap(d=>d.itemsForProcessing)).length ?? 0;
    const coverage = useMemo(() => {
        return prompts.map(prompt => {
            const processedIds = Object.keys(prompt.primitives?.processed ?? {});
            return {
                prompt,
                processed: processedIds.length,
                total: importCount,
            };
        });
    }, [prompts, importCount, update]);

    const handleAddPrompt = async () => {
        if (!selectedCategory) return;
        const cat = mainstore.category(selectedCategory);
        await mainstore.createPrimitive({
            title: cat.title,
            type: "prompt",
            categoryId: selectedCategory,
            parent: primitive,
            workspaceId: primitive.workspaceId,
        });
        setSelectedCategory(null);
        setShowAdd(false);
        forceUpdate();
    };

    const handleDeletePrompt = async (prompt) => {
        mainstore.removePrimitive(prompt);
        forceUpdate();
    };

    const handleStartEdit = (prompt) => {
        setEditingPromptId(prompt.id);
        setEditText(prompt.title ?? "");
    };

    const handleSaveEdit = async (prompt) => {
        if (editText.trim() && editText !== prompt.title) {
            prompt.setField("title", editText.trim());
        }
        setEditingPromptId(null);
        setEditText("");
        forceUpdate();
    };

    const handleCancelEdit = () => {
        setEditingPromptId(null);
        setEditText("");
    };

    const handleRun = () => {
        if (onRun) {
            onRun(primitive);
        } else {
            mainstore.doPrimitiveAction(primitive, "run_question_flow", { flowStarted: new Date().toISOString(), flow: true });
        }
    };

    const overallProcessed = coverage.filter(c => c.processed >= c.total).length;
    const overallTotal = coverage.length;

    return (
        <div className="space-y-3 text-sm">
            {importCount > 0 && (
                <div className="flex items-center gap-2 px-1">
                    <span className="text-xs text-gray-500">
                        Coverage: {overallProcessed}/{overallTotal} prompts fully processed
                    </span>
                    <span className="text-xs text-gray-400">({importCount} docs)</span>
                </div>
            )}

            {prompts.length === 0 && !showAdd && (
                <div className="text-gray-400 text-sm py-4 text-center">
                    No prompts yet. Add one below.
                </div>
            )}

            <div className="space-y-1">
                {prompts.map(prompt => {
                    const cov = coverage.find(c => c.prompt.id === prompt.id);
                    const pct = cov && cov.total > 0 ? Math.round((cov.processed / cov.total) * 100) : 0;
                    const isEditing = editingPromptId === prompt.id;

                    return (
                        <div
                            key={prompt.id}
                            className="flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-950 rounded-md border border-gray-200 dark:border-slate-700 group"
                        >
                            {isEditing ? (
                                <div className="flex-1 flex items-center gap-2">
                                    <Input
                                        size="sm"
                                        value={editText}
                                        onValueChange={setEditText}
                                        className="flex-1"
                                        autoFocus
                                    />
                                    <button onClick={() => handleSaveEdit(prompt)} className="p-1 text-green-500 hover:bg-green-50 rounded">
                                        <CheckIcon className="h-4 w-4" />
                                    </button>
                                    <button onClick={handleCancelEdit} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm truncate">{prompt.title}</p>
                                        {cov && (
                                            <p className="text-xs text-gray-400">
                                                {cov.processed}/{cov.total} docs ({pct}%)
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => handleStartEdit(prompt)}
                                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800"
                                        >
                                            <PencilIcon className="h-3.5 w-3.5 text-gray-400" />
                                        </button>
                                        <button
                                            onClick={() => handleDeletePrompt(prompt)}
                                            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900"
                                        >
                                            <TrashIcon className="h-3.5 w-3.5 text-red-400" />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            {showAdd ? (
                <div className="flex items-center gap-2 px-1">
                    <Select
                        size="sm"
                        className="flex-1"
                        placeholder="Select prompt type"
                        selectedKeys={selectedCategory ? [String(selectedCategory)] : []}
                        onSelectionChange={(keys) => {
                            const val = Array.from(keys)[0];
                            setSelectedCategory(Number(val));
                        }}
                    >
                        {promptCategories.map(cat => (
                            <SelectItem key={String(cat.id)}>{cat.title}</SelectItem>
                        ))}
                    </Select>
                    <Button size="sm" color="primary" onPress={handleAddPrompt} isDisabled={!selectedCategory}>
                        Add
                    </Button>
                    <Button size="sm" variant="light" onPress={() => setShowAdd(false)}>
                        Cancel
                    </Button>
                </div>
            ) : (
                <div className="px-1">
                    <Button
                        size="sm"
                        variant="bordered"
                        startContent={<PlusIcon className="h-4 w-4" />}
                        onPress={() => setShowAdd(true)}
                        className="w-full"
                    >
                        Add prompt
                    </Button>
                </div>
            )}

            <div className="px-1 pt-1">
                <Button
                    size="sm"
                    color="success"
                    startContent={<PlayIcon className="h-4 w-4" />}
                    onPress={handleRun}
                    isDisabled={prompts.length === 0}
                    className="w-full"
                >
                    Run question
                </Button>
            </div>
        </div>
    );
}
